import { useEffect, useMemo } from 'react';
import { Box, capitalizeFirstLetter, TransactionStatus as TransactionStatusType, TransactionType } from 'vopay';
import { useTranslation } from 'react-i18next';
import useTransactionApproval from './useTransactionApproval';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { ExpandableText, TransactionStatus, DetailRowsCard } from 'UI';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';

type ApprovalHistoryDetailsCardProps = {
  transactionID: string;
  status: TransactionStatusType;
  type: TransactionType;
};

type ApprovalHistoryItemType = {
  Username: string;
  ApprovalDecision: 'Approved' | 'Rejected';
  DecisionDate: string;
  Reason: string | null;
};

export default function ApprovalHistoryDetailsCard({ transactionID, status, type }: ApprovalHistoryDetailsCardProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ApprovalHistoryDetailsCard',
  });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();
  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const [transactionTypes, fetchTransactionTypes] = useFetchTransactionTypes({});
  const isPendingApproval = status === 'pending approval';
  const {
    response: { TransactionApprovalWorkflowEnabled, Country },
  } = useClientData();
  const isApprovalWorkflowEnabled = TransactionApprovalWorkflowEnabled !== '0';

  const getApprovalWorkflows = async () => {
    try {
      await fetchWorkflows();
    } catch (err) {
      console.error(err);
      toast.neutral(tGlobal('errorOccurred'));
    }
  };

  useEffect(() => {
    getApprovalWorkflows();

    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, [Country]);

  const approvalSupportedTransactionTypes = useMemo(() => {
    if (!transactionTypes?.TransactionTypes?.length || !workflows?.length) return [];

    return transactionTypes?.TransactionTypes.filter((tt) =>
      workflows.some((w) => w.VIOAccountTransactionTypes_ID === tt.ID),
    ).map((tt) => tt.TransactionType);
  }, [workflows, transactionTypes]);

  const isApprovalSupportedForType = useMemo(() => {
    return approvalSupportedTransactionTypes?.includes(type);
  }, [approvalSupportedTransactionTypes, type]);

  useEffect(() => {
    if (!transactionID || !isApprovalWorkflowEnabled || !isApprovalSupportedForType) {
      return;
    }
    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [transactionID, TransactionApprovalWorkflowEnabled, isApprovalSupportedForType]);

  const approvalHistoryDetails = useMemo(() => {
    const history = approvalHistory ?? [];

    if (isPendingApproval) {
      const num = allApprovalTransactions.find((t) => t.ID.toString() === transactionID)?.NumberApprovalsRequired;
      const required = Number(num) || 0;
      const normalizedApprovalHistory = history.slice(0, required).concat(Array(required - history.length).fill(null));

      return normalizedApprovalHistory.flatMap((item) => {
        if (!item) {
          return [
            {
              label: t('approval', { defaultValue: 'Approval' }),
              value: '-',
            },
            {
              label: t('decision', { defaultValue: 'Decision' }),
              value: '-',
            },
          ];
        }

        return mapApprovalHistoryItem(item);
      });
    }

    return history.flatMap(mapApprovalHistoryItem);
  }, [approvalHistory, allApprovalTransactions, transactionID, isPendingApproval]);

  function mapApprovalHistoryItem(item: ApprovalHistoryItemType) {
    return [
      {
        label: t('approval', { defaultValue: 'Approval' }),
        value: item.Username,
      },
      {
        label: t('decision', { defaultValue: 'Decision' }),
        value: (
          <Box flx="col" gapSize="11px" ai="fe">
            <TransactionStatus
              status={item.ApprovalDecision.toLowerCase()}
              label={capitalizeFirstLetter(
                tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                  defaultValue: item.ApprovalDecision,
                }),
              )}
            />
            <div>{item.DecisionDate}</div>
          </Box>
        ),
      },
      {
        label: t('reason', { defaultValue: 'Reason' }),
        value: <ExpandableText text={item.Reason || ''} />,
        visible: item.Reason !== null,
      },
    ];
  }
  if (!isApprovalWorkflowEnabled) return null;
  if (!isPendingApproval && approvalHistory.length === 0) return null;

  return <DetailRowsCard title={t('title', { defaultValue: 'Approval History' })} details={approvalHistoryDetails} />;
}
